import { Component, Input, OnInit, inject, DestroyRef } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { translate } from '@jsverse/transloco';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { combineLatest } from 'rxjs';
import { Constants } from 'src/app/models/constants';
import { PermissionService } from 'src/app/services/api/permission.service';
import { TenantService } from 'src/app/services/api/tenant.service';
import { SidenavService } from 'src/app/services/util/sidenav.service';
import { CustomToastrService } from 'src/app/services/util/toastr.service';
import cloneDeep from 'lodash/cloneDeep';
import { ConfirmationDialogComponent } from 'src/app/components/confirmation-dialog/confirmation-dialog.component';
import { ViewPermissionsComponent } from '../view-permissions/view-permissions.component';
import { ErrorDialogComponent } from 'src/app/components/error-dialog/error-dialog.component';
import { PermissionValues } from 'src/app/models/permission-values';

@Component({
    selector: 'app-assign-role',
    templateUrl: './assign-role.component.html',
    styleUrls: ['./assign-role.component.scss'],
    standalone: false
})
export class AssignRoleComponent  implements OnInit {
private destroyRef = inject(DestroyRef);
  @Input() name;
  @Input() description;
  @Input() roleAssignments;

  Constants = Constants;
  tenants: any;
  selectedTenant: any;
  searchText: any;
  roles: any;
  tenantRoles: any;
  isLoading = true;
  apiLoading = false;
  filteredRoles: any;
  selectedRoles = [];
  dropdownSearchValue: string;
  initialTenants: any;

  constructor(private fb: FormBuilder, private sidenavService: SidenavService, 
    private dialog: MatDialog, private tenantService: TenantService, 
    private permissionsService: PermissionService, private toastrService: CustomToastrService) {
   
 }

  ngOnInit(): void {
    this.getData();
  }

  getData(): void {
    const globalTenant = { tenantId: "global" };
    combineLatest([this.permissionsService.getRoles(), this.tenantService.getTenants()])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([roles, tenants]) => {
        this.tenants = tenants;
        this.tenants.unshift(globalTenant);
        this.initialTenants = this.tenants;
        this.roles = roles;
        this.roles.map(item => item.selected = false);
        this.isLoading = false;
      }, (error) => {
        setTimeout(() => {
		      const title = translate('ERRORS.SERVICE_AVAILABILITY');
          const message = error?.message ? error?.message : (error?.error?.message ? error?.error?.message : (error?.errmsg ? error?.errmsg : translate('SERVICE.INTERNAL_SERVER_ERROR')));
          this.toastrService.errMessage(message, title);
        }, 500);
      }); 
  }

  closeSidenav(): void {
    this.sidenavService.closeRightSidenav();
  }

  tenantSelectionChanged(value): void {
    const globalRoles = ['platform-admin', 'platform-iam-admin', 'platform-reader', 'tenant-reader', 'solution-publisher'];
    this.selectedTenant = value;
    this.tenantRoles = cloneDeep(this.roles);
    const isGlobal = value.tenantId === "global";
    const getScopeTenant = (scope: string) => scope?.split('/')?.[isGlobal ? 1 : 2];
    const getPrincipalName = (principal: string) => principal?.split('/')?.[2];
    const hasAssignment = (roleName: string) =>
      this.roleAssignments.find(item =>
        item.role === roleName &&
        getScopeTenant(item.scope) === value.tenantId &&
        getPrincipalName(item.principal) === this.name
      );
    this.tenantRoles.forEach(role => {
      role.hidden = false;
      const assignment = hasAssignment(role.name);
      if (assignment) {
        role.selected = true;
        role.disable = true;
        if(!isGlobal && globalRoles.includes(role?.name))
        role.hidden = true;
        return;
      }
      const shouldHide = isGlobal ? !globalRoles.includes(role?.name) : globalRoles.includes(role?.name);
      if (shouldHide) {
        role.hidden = true;
      }
    });
    this.filteredRoles = cloneDeep(this.tenantRoles);
  }

  selectRole(role): void {
    if (role.disable) {
      return;
    }
    if (this.filteredRoles.find(item => item.name === role.name).selected) {
      this.filteredRoles.find(item => item.name === role.name).selected = false;
      this.tenantRoles.find(item => item.name === role.name).selected = false;
    } else {
      this.filteredRoles.find(item => item.name === role.name).selected = true;
      this.tenantRoles.find(item => item.name === role.name).selected = true;
    }
    this.selectedRoles = this.filteredRoles.filter(item => item.selected && !item.disable);
  }

  submit(): void {
    const addedRoles = this.filteredRoles.filter(item => item.selected && !item.disable);
    if (addedRoles?.length > 0) {
      this.dialog.open(ConfirmationDialogComponent, {
        panelClass: 'default-dialog-wrapper',
        width: '566px',
        autoFocus: false,
        data: {
          title: translate('GROUPS.ASSIGN_ROLE_HEADER'),
          message: translate('GROUPS.ASSIGN_ROLE_MESSAGE'),
          confirmButtonText: translate('BUTTONS.CONFIRM')
        }
      })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(async confirm => {
        if (confirm) {
          this.apiLoading = true;
          const promises = [];
          addedRoles.forEach(role => {
            const roleAssignment: any = {};
            roleAssignment.principal = PermissionValues.GROUP + this.name;
            roleAssignment.scope = this.selectedTenant?.tenantId === 'global' ? '/global' : PermissionValues.TENANT + this.selectedTenant?.tenantId;
            roleAssignment.role = role.name;
            const postedRole = this.postRoleAssignment(roleAssignment);
            promises.push(postedRole);
          });
          await Promise.allSettled(promises).then((rolesResponses: any) => {
            rolesResponses = rolesResponses.map((version: any) => version.value);
            this.apiLoading = false;
            if (rolesResponses[0].httpStatusCode === 200 || rolesResponses[0].httpStatusCode === 201) {
              this.sidenavService.closeRightSidenav(true);
            setTimeout(() => {
              const title = translate('GROUPS.GROUPS');
              const message = translate('GROUPS.ASSIGN_ROLE_SUCCESS');
              this.toastrService.successMessage(message, title);
            }, 500);
            }
          });
        }
      });
    }
  }

  postRoleAssignment(role): Promise<any> {
    return new Promise((resolve) => {
      this.permissionsService.postRoleAssignment(role)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(result => {
        resolve(result);
      }, (error) => {
        resolve(error);
        const errorMessage = typeof error === 'string' ? error: typeof error?.error === 'string' ? error.error : error;
        this.dialog.open(ErrorDialogComponent, {
          minHeight: '342px',
          width: '630px',
          panelClass: 'flex-dialog',
          data: {
            title:  translate('GROUPS.ASSINGING_ROLE_FAILED'),
            description: errorMessage,
            hideReportButton: true
          }
        });
      });
    });
  }

  searchRole(event?): void {
    if (event) {
      this.searchText = event.target.value;
    }
    if (this.searchText) {
      this.filteredRoles = this.tenantRoles.filter(item => item.name.toLocaleLowerCase().includes(this.searchText.toLocaleLowerCase()));
    } else {
      this.filteredRoles = this.tenantRoles;
    }
  }

  viewPermissions(role) {
    role.tenant = this.selectedTenant?.tenantId;
    this.dialog.open(ViewPermissionsComponent, {
      panelClass: 'default-dialog-wrapper',
      width: '50vw',
      autoFocus: false,
      data: role
    });
  }

  resetDropdown() {
    setTimeout(() => {
    this.dropdownSearchValue = '';
    this.tenants = [...this.initialTenants];
    }, 100);
  }

  searchChanged(value): void {
    this.tenants = this.initialTenants?.filter(item => item?.tenantId?.toLowerCase().startsWith(value.toLowerCase()));
  }
}
